import { useEffect, useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import type { AgentApiProfitsShow } from '@/types/serializers';
import { currentSelectProfitAtom } from '../atoms/profit';
import useFetchProfit from '../api/useFetchProfit';
import useUpdateProfit from '../api/useUpdateProfit';
import { profitUpdateSchema, type ProfitUpdateInput } from '../schemas/profitFormSchema';
import type { ProfitAction } from './ProfitStatusMenu';
import Loading from '@/components/Loading';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import DatePickerModal from '@/components/DatePickerModal';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import FormLabel from '@/components/auth/FormLabel';
import { MaterialIcon } from '@/components/MaterialIcon';
import { showErrorToast, showSuccessToast } from '@/features/agent/job_change_processes/lib/toast';

type Props = {
  closeSidebar: () => void;
  onAction: (action: ProfitAction) => void;
  isSidebarOpen: boolean;
  maxHeight?: string;
};

type StatusOption = {
  key: ProfitAction;
  label: string;
  color: 'default' | 'primary' | 'danger';
};

function getStatusOptions(profit: AgentApiProfitsShow): StatusOption[] {
  switch (profit.status) {
    case 'waiting_entry':
    case 'invoice_sent': {
      const items: StatusOption[] = [
        { key: 'confirmEntry', label: '入社確定', color: 'primary' },
        { key: 'candidateDecline', label: '求職者辞退', color: 'danger' },
        { key: 'companyDecline', label: '企業NG', color: 'danger' },
      ];
      if (profit.deletable) items.push({ key: 'delete', label: '削除', color: 'danger' });
      return items;
    }
    case 'waiting_payment':
      return [
        { key: 'payment', label: '入金確定', color: 'primary' },
        { key: 'candidateDecline', label: '求職者辞退', color: 'danger' },
        { key: 'companyDecline', label: '企業NG', color: 'danger' },
      ];
    case 'paid':
      return [{ key: 'refund', label: '返金処理', color: 'default' }];
    case 'refund':
      return [{ key: 'refunded', label: '返金確定', color: 'danger' }];
    default:
      return [];
  }
}

const statusOptionColorClass: Record<StatusOption['color'], string> = {
  default: 'text-main',
  primary: 'text-primary',
  danger: 'text-danger',
};

export default function ProfitSidebar({
  closeSidebar,
  onAction,
  isSidebarOpen: _isSidebarOpen,
  maxHeight = 'calc(100vh - 260px)',
}: Props) {
  const currentSelectProfit = useAtomValue(currentSelectProfitAtom);
  const queryClient = useQueryClient();
  const { data: profit, isLoading } = useFetchProfit({
    id: currentSelectProfit?.id ?? 0,
  });
  const updateProfit = useUpdateProfit();

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ProfitUpdateInput>({
    resolver: zodResolver(profitUpdateSchema),
    defaultValues: {
      profit: {
        profit_value: 0,
        entry_expect_date: '',
        payment_date: '',
        refund_date: '',
        memo: '',
        organization_user_id: 0,
      },
    },
  });

  useEffect(() => {
    if (!profit) return;
    reset({
      profit: {
        profit_value: profit.profit_value,
        entry_expect_date: profit.entry_expect_date || '',
        payment_date: profit.payment_date || '',
        refund_date: profit.refund_date || '',
        memo: profit.memo || '',
        organization_user_id: profit.organization_user?.id ?? 0,
      },
    });
  }, [profit, reset]);

  const statusOptions = useMemo(() => (profit ? getStatusOptions(profit) : []), [profit]);

  if (!currentSelectProfit) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-sm text-sub">売上票を選択してください</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <Loading size="medium" />
      </div>
    );
  }

  if (!profit) {
    return (
      <div className="flex h-full items-center justify-center bg-white">
        <p className="text-sm text-sub">データの読み込みに失敗しました</p>
      </div>
    );
  }

  const isRefundFlow = profit.status === 'refund' || profit.status === 'refunded';
  const editable = profit.editable_profit_value;
  const candidateId = profit.candidate_profile?.id;
  const companyId = profit.company?.id;
  const positionName = profit.job_position_name;
  const providedJobPostUrl = profit.provided_job_post?.urls?.show ?? null;
  const statusDisabled = statusOptions.length === 0;

  const handleSave = async (input: ProfitUpdateInput) => {
    try {
      await updateProfit.mutateAsync({ id: profit.id, ...input });
      queryClient.invalidateQueries({ queryKey: ['/agent/api/profits'] });
      queryClient.invalidateQueries({ queryKey: ['/agent/api/profits', profit.id] });
      showSuccessToast('売上票を更新しました');
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'エラーが発生しました');
    }
  };

  return (
    <div className={maxHeight ? 'flex flex-col bg-white' : 'flex h-full flex-col bg-white'}>
      <div className="flex h-[57px] shrink-0 items-center justify-between py-2 pr-4 pl-3">
        <div className="flex min-w-0 items-center">
          <button
            type="button"
            onClick={closeSidebar}
            className="flex size-[42px] items-center justify-center"
            aria-label="サイドバーを閉じる"
          >
            <img
              src="/agent/job_change_processes/icon-arrow-right.svg"
              width={16}
              height={16}
              alt=""
            />
          </button>
          {candidateId ? (
            <Link
              to="/agent/candidates/$candidateProfileId"
              params={{ candidateProfileId: String(candidateId) }}
              className="min-w-0 truncate text-base font-bold text-primary hover:underline"
            >
              {profit.candidate_profile?.name || '-'}
            </Link>
          ) : (
            <p className="min-w-0 truncate text-base font-bold text-primary">
              {profit.candidate_profile?.name || '-'}
            </p>
          )}
        </div>
      </div>

      <div className="px-7 pb-3">
        <div className="flex flex-wrap items-center gap-xs">
          {companyId ? (
            <Link
              to="/agent/companies/$companiesId"
              params={{ companiesId: String(companyId) }}
              className="text-sm font-bold text-primary hover:underline"
            >
              {profit.company?.name || '-'}
            </Link>
          ) : (
            <span className="text-sm font-bold text-primary">{profit.company?.name || '-'}</span>
          )}
          {positionName && (
            <>
              <span className="text-sm text-sub">/</span>
              {providedJobPostUrl ? (
                <Link
                  to={providedJobPostUrl}
                  className="text-sm font-bold text-primary hover:underline"
                >
                  {positionName}
                </Link>
              ) : (
                <span className="text-sm font-bold text-primary">{positionName}</span>
              )}
            </>
          )}
        </div>
      </div>

      <form
        onSubmit={handleSubmit(handleSave)}
        className={
          maxHeight
            ? 'hidden-scrollbar flex flex-col overflow-y-auto'
            : 'hidden-scrollbar flex flex-1 flex-col overflow-y-auto'
        }
        style={maxHeight ? { maxHeight } : undefined}
      >
        <div className="flex flex-col gap-md px-7 pt-sm pb-6">
          {profit.payment_overdue && (
            <div className="flex items-center gap-xs rounded-sm border border-solid border-danger bg-danger_lighten px-md py-sm">
              <MaterialIcon name="error" className="text-xl text-danger" />
              <span className="text-sm leading-[1.7] text-main">
                入金予定日が過ぎています。入金確認を行なってください。
              </span>
            </div>
          )}

          <div className="flex flex-col gap-xs">
            <FormLabel>ステータス</FormLabel>
            <Select
              disabled={statusDisabled}
              value=""
              onValueChange={(value) => onAction(value as ProfitAction)}
            >
              <SelectTrigger
                className={clsx(
                  'h-[42px] text-sm text-main',
                  statusDisabled && 'cursor-not-allowed opacity-60'
                )}
              >
                <span className="truncate">{profit.status_title || '-'}</span>
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem
                    key={option.key}
                    value={option.key}
                    className={statusOptionColorClass[option.color]}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-xs">
            <FormLabel required htmlFor="profit_value">
              売上金額
            </FormLabel>
            <Input
              id="profit_value"
              type="number"
              className="h-[42px] w-full [-moz-appearance:textfield] disabled:bg-gray-100 disabled:text-sub [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
              disabled={!editable}
              {...register('profit.profit_value', { valueAsNumber: true })}
            />
            {errors.profit?.profit_value && (
              <p className="mt-1 text-xs text-danger">{errors.profit.profit_value.message}</p>
            )}
          </div>

          <div className="flex flex-col gap-xs">
            <FormLabel required>入社予定日</FormLabel>
            <Controller
              control={control}
              name="profit.entry_expect_date"
              render={({ field }) => (
                <DatePickerModal value={field.value || ''} onChange={field.onChange} />
              )}
            />
            {errors.profit?.entry_expect_date && (
              <p className="mt-1 text-xs text-danger">{errors.profit.entry_expect_date.message}</p>
            )}
          </div>

          {isRefundFlow ? (
            <div className="flex flex-col gap-xs">
              <FormLabel>返金予定日</FormLabel>
              <Controller
                control={control}
                name="profit.refund_date"
                render={({ field }) => (
                  <DatePickerModal value={field.value || ''} onChange={field.onChange} />
                )}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-xs">
              <FormLabel>入金予定日</FormLabel>
              <Controller
                control={control}
                name="profit.payment_date"
                render={({ field }) => (
                  <DatePickerModal value={field.value || ''} onChange={field.onChange} />
                )}
              />
            </div>
          )}

          <div className="flex flex-col gap-xs">
            <FormLabel htmlFor="memo">メモ</FormLabel>
            <Textarea
              id="memo"
              rows={5}
              error={errors.profit?.memo?.message}
              {...register('profit.memo')}
            />
          </div>

          <div className="flex justify-end pt-sm">
            <Button type="submit" color="primary" size="md" disabled={updateProfit.isPending}>
              {updateProfit.isPending ? '保存中...' : '保存する'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
